import { useState, useEffect, useMemo } from 'react';
import type { StepContentProps } from '~/ui-rocco/multi-step-form';
import { StepContent } from '../StepContent';
import { HdeOptionList } from '../HDEOptionList';
import { getScopesByProjectType } from '@modules/build-hub/configs/stage-config';
import type { GroupedSelectOptionType } from '../../../utils/form-options';
import type { StageConfiguration } from '@modules/build-hub/configs/stage-config';

const HDE_BUDGET_OPTIONS: GroupedSelectOptionType[] = [
    { label: 'Under $200K', value: 'Under $200K' },
    { label: '$200K-$500K', value: '$200K-$500K' },
    { label: '$500K-$1M', value: '$500K-$1M' },
    { label: '$1M+', value: '$1M+' },
    { label: 'Prefer not to say', value: 'Prefer not to say' },
];

interface ProjectBudgetData {
    budget: string | null;
    projectScope: string | null;
    projectType: 'Residential' | 'Commercial' | null;
    stageConfig?: StageConfiguration;
}

export function ProjectBudgetStep({
    setIsValid,
    onDataChange,
    onNext,
    initialData,
}: StepContentProps) {
    const data = initialData as ProjectBudgetData | undefined;
    const [budget, setBudget] = useState<string | null>(data?.budget || null);

    const projectScope = data?.projectScope ?? null;
    const projectType = data?.projectType ?? 'Residential';
    const stageConfig = data?.stageConfig;

    const budgetOptions = useMemo<GroupedSelectOptionType[]>(() => {
        if (projectScope && stageConfig && projectType) {
            const scopes = getScopesByProjectType(
                stageConfig,
                projectType as 'Residential' | 'Commercial',
            );
            const scopeConfig = scopes.find(
                (s) => s.value === projectScope || s.label === projectScope,
            );

            if (scopeConfig?.budgetOptions?.length) {
                return scopeConfig.budgetOptions
                    .filter(
                        (opt) =>
                            opt.key !== 'budget_custom' &&
                            opt.key !== 'custom' &&
                            !opt.label.toLowerCase().includes('custom'),
                    )
                    .map((opt) => ({
                        label: opt.label,
                        value: opt.key,
                    }));
            }
        }

        return HDE_BUDGET_OPTIONS;
    }, [projectScope, projectType, stageConfig]);

    // Reset budget when selected option is no longer in the list (e.g. user changed scope)
    useEffect(() => {
        if (budget && budgetOptions.length > 0) {
            const isValidOption = budgetOptions.some(
                (opt) => opt.value === budget,
            );
            if (!isValidOption) {
                setBudget(null);
            }
        }
    }, [budget, budgetOptions]);

    useEffect(() => {
        const isValid = budget !== null;
        setIsValid(isValid);
        onDataChange?.({ budget });
    }, [budget, setIsValid, onDataChange]);

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <StepContent>
                <HdeOptionList
                    label="What is your Project Budget? *"
                    options={budgetOptions}
                    mode="single"
                    value={budget}
                    onChange={setBudget}
                    appearance="arrow"
                    onSelectAndAdvance={(value) => {
                        onDataChange?.({ budget: value });
                        onNext();
                    }}
                />
            </StepContent>
        </div>
    );
}
